import { clamp } from 'lodash-es';
import { VPT } from 'src/app/state/temporary-user-metadata-repository.service';

/** Width of the whiteboard column in board px (board x 0..1200). Luna writes inside it. */
export const WORKSPACE_COLUMN_WIDTH = 1200;
/** Zoom floor that keeps Luna's 18px text at about 12px on narrow screens. */
export const WORKSPACE_READABLE_MIN_ZOOM = 0.67;
/** The furthest the student (or a drawing Luna shows whole) may zoom out below the readable floor. */
export const WORKSPACE_FIT_MIN_ZOOM = 0.5;
/** The whiteboard's existing 400% limit (`MAX_ZOOM` in sessions-vpt.service). */
export const WORKSPACE_MAX_ZOOM = 4;
/** Screen px of empty space allowed above board y = 0. */
export const WORKSPACE_TOP_INSET = 24;
/** Fraction of a stage height the student can scroll past the last content. */
export const WORKSPACE_RUNWAY_FRACTION = 0.5;
/** Px between the whiteboard and the app in split view. */
export const WORKSPACE_SPLIT_GAP = 12;

/** The visible whiteboard area, in canvas px. */
export interface StageRect {
  left: number;
  top: number;
  width: number;
  height: number;
  /**
   * The width the column is sized for, when it differs from the visible width: the whiteboard
   * width with the Luna panel open, so toggling the panel never rescales the board.
   */
  fitWidth?: number;
}

export interface ColumnPolicy {
  columnWidth: number;
  readableMinZoom: number;
  fitMinZoom: number;
  maxZoom: number;
  topInset: number;
  runwayFraction: number;
}

export const WORKSPACE_COLUMN_POLICY: ColumnPolicy = {
  columnWidth: WORKSPACE_COLUMN_WIDTH,
  readableMinZoom: WORKSPACE_READABLE_MIN_ZOOM,
  fitMinZoom: WORKSPACE_FIT_MIN_ZOOM,
  maxZoom: WORKSPACE_MAX_ZOOM,
  topInset: WORKSPACE_TOP_INSET,
  runwayFraction: WORKSPACE_RUNWAY_FRACTION,
};

/** The column fills the stage width, unless that would drop below the readable floor. */
export function workspaceMinZoom(
  stage: StageRect,
  policy: ColumnPolicy = WORKSPACE_COLUMN_POLICY,
): number {
  return Math.max((stage.fitWidth ?? stage.width) / policy.columnWidth, policy.readableMinZoom);
}

/**
 * The furthest the camera may zoom out: the minimum, or on a window too narrow for the column at
 * a readable zoom, far enough to see the whole column (a drawing as wide as it), down to 50%.
 */
export function workspaceFloorZoom(
  stage: StageRect,
  policy: ColumnPolicy = WORKSPACE_COLUMN_POLICY,
): number {
  const columnFit = (stage.fitWidth ?? stage.width) / policy.columnWidth;
  return Math.min(workspaceMinZoom(stage, policy), Math.max(columnFit, policy.fitMinZoom));
}

/**
 * The zoom a zoom gesture may ask for. Clamping it before fabric zooms around the cursor keeps
 * the point under the cursor still; clamping afterwards would keep a pan meant for another zoom.
 */
export function clampRequestedZoom(
  zoom: number,
  stage: StageRect,
  policy: ColumnPolicy = WORKSPACE_COLUMN_POLICY,
): number {
  return clamp(zoom, workspaceFloorZoom(stage, policy), policy.maxZoom);
}

/**
 * Keeps the camera on the column: zoom never below the minimum, sideways movement only while
 * the column is wider than the stage and never past its edges, and vertical movement between
 * the top of the board and the content bottom plus a runway.
 */
export function constrainVpt(
  vpt: VPT,
  stage: StageRect,
  contentBottom: number,
  policy: ColumnPolicy = WORKSPACE_COLUMN_POLICY,
): VPT {
  const zoom = clamp(vpt[0], workspaceFloorZoom(stage, policy), policy.maxZoom);
  const columnPx = policy.columnWidth * zoom;
  // Pinned left when it fits, so extra width (e.g. the panel closing) appears on the right
  // without moving anything.
  const tx =
    columnPx <= stage.width
      ? stage.left
      : clamp(vpt[4], stage.left + stage.width - columnPx, stage.left);

  const maxTy = stage.top + policy.topInset;
  const bottom = scrollBottom(stage, contentBottom, zoom, policy);
  const minTy = stage.top + stage.height - bottom * zoom;
  const ty = clamp(vpt[5], Math.min(minTy, maxTy), maxTy);

  return [zoom, 0, 0, zoom, tx, ty];
}

/** The lowest board y the view may reach: content plus runway, and at least one stage tall. */
function scrollBottom(
  stage: StageRect,
  contentBottom: number,
  zoom: number,
  policy: ColumnPolicy,
): number {
  // At least one stage tall below the top inset, so content that fits never moves.
  return Math.max(
    contentBottom + (policy.runwayFraction * stage.height) / zoom,
    (stage.height - policy.topInset) / zoom,
  );
}

/** A scrollbar thumb as fractions of its track; null when there is nothing to scroll. */
export interface ScrollThumb {
  start: number;
  size: number;
}

export interface ScrollIndicators {
  vertical: ScrollThumb | null;
  horizontal: ScrollThumb | null;
}

/** Where the whiteboard's scrollbar thumbs sit, from the same limits the camera rule uses. */
export function scrollIndicators(
  vpt: VPT,
  stage: StageRect,
  contentBottom: number,
  policy: ColumnPolicy = WORKSPACE_COLUMN_POLICY,
): ScrollIndicators {
  const zoom = vpt[0];
  const epsilon = 1e-6;

  const totalTop = -policy.topInset / zoom;
  const total = scrollBottom(stage, contentBottom, zoom, policy) - totalTop;
  const visibleHeight = stage.height / zoom;
  const visibleTop = (stage.top - vpt[5]) / zoom;
  const vertical =
    visibleHeight >= total - epsilon
      ? null
      : { start: clamp((visibleTop - totalTop) / total, 0, 1), size: visibleHeight / total };

  const visibleWidth = stage.width / zoom;
  const visibleLeft = (stage.left - vpt[4]) / zoom;
  const horizontal =
    visibleWidth >= policy.columnWidth - epsilon
      ? null
      : {
          start: clamp(visibleLeft / policy.columnWidth, 0, 1),
          size: visibleWidth / policy.columnWidth,
        };

  return { vertical, horizontal };
}

/**
 * Scales the zoom with the stage, keeping it the same multiple of the minimum zoom (so the
 * column still fills the width after a resize, even across the readable floor), and keeps the
 * board point at the stage's top-left in place, so the student keeps their place.
 */
export function rescaleVptForStage(
  vpt: VPT,
  from: StageRect,
  to: StageRect,
  policy: ColumnPolicy = WORKSPACE_COLUMN_POLICY,
): VPT {
  if (!from.width || !to.width) {
    return vpt;
  }
  const zoom = vpt[0];
  const nextZoom = zoom * (workspaceMinZoom(to, policy) / workspaceMinZoom(from, policy));
  const boardLeft = (from.left - vpt[4]) / zoom;
  const boardTop = (from.top - vpt[5]) / zoom;
  return [nextZoom, 0, 0, nextZoom, to.left - boardLeft * nextZoom, to.top - boardTop * nextZoom];
}

/** A full-screen app tab fills the whiteboard area under the tab bar; the tabs replace its bar. */
export function workspaceFullAppBounds(stage: StageRect): {
  top: 0;
  width: string;
  height: string;
  hideInteractionBar: true;
} {
  return {
    top: 0,
    width: `${stage.width}px`,
    height: `${stage.height}px`,
    hideInteractionBar: true,
  };
}

/** Anything with fabric's `getBoundingRect`. */
export interface BoundedObject {
  getBoundingRect(absolute?: boolean, calculate?: boolean): { top: number; height: number };
}

/** The lowest board y of the writing on the board; apps live in the tabs, so they don't count. */
export function contentBottomOf<T extends BoundedObject>(
  objects: readonly T[],
  isApp: (object: T) => boolean,
): number {
  return objects.reduce((bottom, object) => {
    if (isApp(object)) {
      return bottom;
    }
    const rect = object.getBoundingRect(true, true);
    return Math.max(bottom, rect.top + rect.height);
  }, 0);
}

/** Whiteboard on the left, app on the right, split by a gap. */
export function splitStage(
  whiteboard: StageRect,
  gap: number = WORKSPACE_SPLIT_GAP,
): { board: StageRect; app: StageRect } {
  const boardWidth = Math.floor((whiteboard.width - gap) / 2);
  const appLeft = whiteboard.left + boardWidth + gap;
  return {
    board: {
      left: whiteboard.left,
      top: whiteboard.top,
      width: boardWidth,
      height: whiteboard.height,
    },
    app: {
      left: appLeft,
      top: whiteboard.top,
      width: whiteboard.left + whiteboard.width - appLeft,
      height: whiteboard.height,
    },
  };
}

/** The Luna panel's width range and its share of the screen: clamp(360px, 26vw, 480px). */
export const LUNA_PANEL_MIN_WIDTH = 360;
export const LUNA_PANEL_MAX_WIDTH = 480;
const LUNA_PANEL_SCREEN_SHARE = 0.26;

/**
 * The Luna panel's width for a screen this wide: 360px on a laptop, growing with the screen,
 * up to 480px on big monitors, so her replies keep a comfortable line length. The panel and
 * the whiteboard camera both use this, so opening or closing the panel never changes the zoom.
 */
export function lunaPanelWidth(viewportWidth: number): number {
  return Math.min(
    LUNA_PANEL_MAX_WIDTH,
    Math.max(LUNA_PANEL_MIN_WIDTH, Math.round(viewportWidth * LUNA_PANEL_SCREEN_SHARE)),
  );
}
